import { useEffect, useState, FormEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Wallet as WalletIcon,
  ShieldAlert,
  Loader2,
  Snowflake,
  Sun,
} from 'lucide-react';
import { toast } from 'sonner';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  useWalletLedgerQuery,
  useWalletLookupQuery,
} from '@/client/api/payment/payment.query';
import {
  useAdjustWalletMutation,
  useFreezeWalletMutation,
  useUnfreezeWalletMutation,
} from '@/client/api/payment/payment.mutation';
import { formatMoney, Wallet, WalletDirection } from '@/client/types/payment';
import { usePaymentListUrlState } from '@/client/hooks/usePaymentListUrlState';
import { useBreadcrumbs } from '@/client/hooks/use-breadcrumbs';
import { useHasPermission } from '@/client/hooks/useHasPermission';
import { generateIdempotencyKey } from '@/client/utils/requestSecurity';
import {
  OrgRelationshipLink,
  RelationshipLink,
  UserRelationshipLink,
} from '@/client/components/admin/Payment/RelationshipLink';
import Pagination from '@/client/components/common/Pagination';
import {
  WalletBrowse,
  WalletFeatureSwitches,
  WalletPayoutConfigCard,
  WalletWithdrawals,
} from './WalletPanels';

const formatDate = (raw?: string | null) => {
  if (!raw) return '—';
  try {
    return new Date(raw).toLocaleString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return raw;
  }
};

const AccessDenied = () => (
  <div className="flex flex-col items-center justify-center gap-3 rounded-xl border border-gray-100 bg-white py-20 text-center shadow-sm">
    <div className="flex h-14 w-14 items-center justify-center rounded-full bg-red-50">
      <ShieldAlert className="h-7 w-7 text-red-500" />
    </div>
    <h2 className="text-lg font-semibold text-gray-800">Access restricted</h2>
    <p className="max-w-sm text-sm text-gray-500">
      Viewing wallets requires the{' '}
      <code className="rounded bg-gray-100 px-1.5 py-0.5 text-xs">payment.view</code> permission.
    </p>
  </div>
);

const LEDGER_LIMIT = 25;

const TABS = [
  { id: 'wallets', label: 'Wallets' },
  { id: 'withdrawals', label: 'Withdrawals' },
  { id: 'switches', label: 'Feature switches' },
] as const;
type TabId = (typeof TABS)[number]['id'];

const WalletPage = () => {
  useBreadcrumbs([
    { label: 'Payments', href: '/admin/payments' },
    { label: 'Wallets' },
  ]);

  const viewPerm = useHasPermission('payment.view');
  const adjustPerm = useHasPermission('payment.adjust');

  const canView = viewPerm.isSuperAdmin || viewPerm.hasPermission;
  const canAdjust = adjustPerm.isSuperAdmin || adjustPerm.hasPermission;
  const permLoading = viewPerm.isLoading;

  const { state, patch } = usePaymentListUrlState();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const tab: TabId = TABS.some((t) => t.id === tabParam) ? (tabParam as TabId) : 'wallets';
  const setTab = (id: TabId) =>
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (id === 'wallets') next.delete('tab');
        else next.set('tab', id);
        return next;
      },
      { replace: true },
    );

  const uid = state.uid ? Number(state.uid) : undefined;
  const oid = state.oid ? Number(state.oid) : undefined;
  const wid = state.wid ?? undefined;
  const ccode = state.ccode || undefined;
  const hasTarget = !!(uid || oid || wid);

  const [selectedWid, setSelectedWid] = useState<number | null>(null);
  const [direction, setDirection] = useState<WalletDirection>('credit');
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);

  const [ledgerType, setLedgerType] = useState('');
  const [ledgerPage, setLedgerPage] = useState(1);

  const { data: lookup, isLoading: walletLoading, isFetching } = useWalletLookupQuery(
    { uid, oid, ccode, wid },
    hasTarget && canView,
  );

  const wallets = lookup?.wallets ?? [];

  useEffect(() => {
    if (!wallets.length) {
      setSelectedWid(null);
      return;
    }
    const preferred =
      (wid && wallets.find((w) => w.wid === wid)) ||
      (ccode && wallets.find((w) => w.ccode === ccode)) ||
      wallets.find((w) => w.ccode === 'GHS') ||
      wallets[0];
    setSelectedWid(preferred?.wid ?? null);
  }, [wallets, wid, ccode]);

  useEffect(() => {
    setLedgerPage(1);
  }, [selectedWid, ledgerType]);

  const selectedWallet: Wallet | null = wallets.find((w) => w.wid === selectedWid) ?? null;

  const { data: ledger, isLoading: ledgerLoading } = useWalletLedgerQuery(
    selectedWallet?.wid ?? null,
    {
      ...(ledgerType ? { type: ledgerType } : {}),
      limit: LEDGER_LIMIT,
      offset: (ledgerPage - 1) * LEDGER_LIMIT,
    },
    !!selectedWallet?.wid,
  );

  const adjustMutation = useAdjustWalletMutation();
  const freezeMutation = useFreezeWalletMutation();
  const unfreezeMutation = useUnfreezeWalletMutation();

  const handleLookup = (e: FormEvent) => {
    e.preventDefault();
    if (!state.uid.trim() && !state.oid.trim() && !state.wid) {
      toast.error('Enter a UID, OID, or WID', { richColors: true });
      return;
    }
  };

  const validateAdjustment = () => {
    const amt = Number(amount);
    if (!Number.isFinite(amt) || amt <= 0) {
      toast.error('Enter a valid amount', { richColors: true });
      return false;
    }
    if (!reason.trim()) {
      toast.error('Reason is required', { richColors: true });
      return false;
    }
    return true;
  };

  const handleAdjustSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!canAdjust) return;
    if (!validateAdjustment()) return;
    setConfirmOpen(true);
  };

  const handleAdjustConfirmed = async () => {
    const amt = Number(amount);
    try {
      await adjustMutation.mutateAsync({
        body: {
          direction,
          amount: amt,
          reason: reason.trim(),
          ...(selectedWallet?.wid ? { wid: selectedWallet.wid } : {}),
          ...(uid ? { uid } : {}),
          ...(oid ? { oid } : {}),
          ccode: selectedWallet?.ccode || ccode || 'GHS',
        },
        idempotencyKey: generateIdempotencyKey(),
      });
      toast.success('Wallet adjusted', { richColors: true });
      setAmount('');
      setReason('');
      setConfirmOpen(false);
    } catch (error: any) {
      toast.error(error?.message || 'Failed to adjust wallet', { richColors: true });
    }
  };

  const isHeld = selectedWallet?.hold === 1 || selectedWallet?.hold === true;

  if (permLoading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="mx-auto max-w-screen-2xl space-y-4">
          <Skeleton className="h-10 w-48" />
          <Skeleton className="h-24 w-full" />
        </div>
      </div>
    );
  }

  if (!canView) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="mx-auto max-w-screen-2xl">
          <AccessDenied />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="mx-auto max-w-screen-2xl space-y-6">
        <div>
          <div className="mb-1 flex items-center gap-2.5">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-600">
              <WalletIcon className="h-4 w-4 text-white" />
            </div>
            <h1 className="text-2xl font-black text-gray-900">Wallets</h1>
          </div>
          <p className="text-sm text-gray-500">
            Browse every wallet, look one up by UID or OID, adjust, freeze, resolve withdrawals and
            switch wallet features on or off.
          </p>
        </div>

        <div className="flex gap-1 border-b border-gray-200" role="tablist">
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={tab === t.id}
              onClick={() => setTab(t.id)}
              className={`-mb-px border-b-2 px-4 py-2 text-sm font-medium transition-colors ${
                tab === t.id
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-800'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === 'withdrawals' && <WalletWithdrawals canAct={canAdjust} />}
        {tab === 'switches' && <WalletFeatureSwitches />}

        {/* Wallets tab — the lookup form and everything below it. */}
        {tab === 'wallets' && (
        <>
        <form
          onSubmit={handleLookup}
          className="rounded-xl border border-gray-100 bg-white p-5 shadow-sm"
        >
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-4">
            <div className="space-y-1.5">
              <Label>User ID</Label>
              <Input
                value={state.uid}
                onChange={(e) => patch({ uid: e.target.value }, false)}
                placeholder="User ID"
                inputMode="numeric"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Organization ID</Label>
              <Input
                value={state.oid}
                onChange={(e) => patch({ oid: e.target.value }, false)}
                placeholder="Organization ID"
                inputMode="numeric"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Currency (optional)</Label>
              <Input
                value={state.ccode}
                onChange={(e) => patch({ ccode: e.target.value.toUpperCase() }, false)}
                placeholder="Leave blank for all currencies"
              />
            </div>
            <div className="flex items-end">
              <Button type="submit" className="w-full" disabled={isFetching}>
                {isFetching && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Look up
              </Button>
            </div>
          </div>
        </form>

        {!hasTarget && <WalletBrowse onOpen={(w) => patch({ wid: w }, false)} />}

        {hasTarget && (
          <button
            type="button"
            className="text-sm text-blue-600 hover:underline"
            onClick={() => patch({ uid: '', oid: '', ccode: '', wid: null }, false)}
          >
            ← All wallets
          </button>
        )}

        {hasTarget && walletLoading && (
          <div className="space-y-3">
            <Skeleton className="h-28 w-full" />
            <Skeleton className="h-40 w-full" />
          </div>
        )}

        {hasTarget && !walletLoading && !wallets.length && (
          <div className="rounded-lg border border-amber-100 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            No wallet found for this lookup.
          </div>
        )}

        {hasTarget && !walletLoading && wallets.length > 0 && (
          <>
            <div className="rounded-xl border border-gray-100 bg-white p-5 shadow-sm">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex flex-wrap items-center gap-3">
                  {uid != null && <UserRelationshipLink uid={uid} summary={lookup?.user_summary} />}
                  {oid != null && <OrgRelationshipLink oid={oid} summary={lookup?.org_summary} />}
                </div>
                <RelationshipLink
                  kind="card"
                  id={uid ?? oid ?? ''}
                  label="View saved cards"
                  extra={{ uid, oid }}
                />
              </div>

              {wallets.length > 1 && (
                <div className="mt-4 flex flex-wrap gap-2 border-b border-gray-100 pb-3">
                  {wallets.map((w) => (
                    <button
                      key={w.wid}
                      type="button"
                      onClick={() => patch({ wid: w.wid, ccode: w.ccode }, false)}
                      className={`rounded-full px-3 py-1.5 text-sm font-medium transition-colors ${
                        selectedWid === w.wid
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      {w.ccode || 'GHS'}
                    </button>
                  ))}
                </div>
              )}

              {selectedWallet && (
                <div className="mt-4 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <p className="text-xs text-gray-500">Wallet #{selectedWallet.wid}</p>
                    <p className="mt-1 text-3xl font-black text-gray-900">
                      {formatMoney(selectedWallet.balance, selectedWallet.ccode)}
                    </p>
                    <p className="mt-1 text-sm text-gray-500">
                      Hold: {isHeld ? 'frozen' : 'none'}
                    </p>
                  </div>
                  {canAdjust && (
                    <div className="flex gap-2">
                      {!isHeld ? (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          disabled={freezeMutation.isPending}
                          onClick={async () => {
                            try {
                              await freezeMutation.mutateAsync(selectedWallet.wid);
                              toast.success('Wallet frozen', { richColors: true });
                            } catch (error: any) {
                              toast.error(error?.message || 'Failed to freeze', { richColors: true });
                            }
                          }}
                        >
                          {freezeMutation.isPending ? (
                            <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                          ) : (
                            <Snowflake className="mr-1.5 h-4 w-4" />
                          )}
                          Freeze
                        </Button>
                      ) : (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          disabled={unfreezeMutation.isPending}
                          onClick={async () => {
                            try {
                              await unfreezeMutation.mutateAsync(selectedWallet.wid);
                              toast.success('Wallet unfrozen', { richColors: true });
                            } catch (error: any) {
                              toast.error(error?.message || 'Failed to unfreeze', {
                                richColors: true,
                              });
                            }
                          }}
                        >
                          {unfreezeMutation.isPending ? (
                            <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                          ) : (
                            <Sun className="mr-1.5 h-4 w-4" />
                          )}
                          Unfreeze
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            {canAdjust && selectedWallet && (
              <form
                onSubmit={handleAdjustSubmit}
                className="rounded-xl border border-gray-100 bg-white p-5 shadow-sm"
              >
                <h2 className="mb-3 text-sm font-semibold text-gray-800">Adjust balance</h2>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-4">
                  <div className="space-y-1.5">
                    <Label>Direction</Label>
                    <select
                      value={direction}
                      onChange={(e) => setDirection(e.target.value as WalletDirection)}
                      className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm"
                    >
                      <option value="credit">Credit</option>
                      <option value="debit">Debit</option>
                      <option value="hold">Hold</option>
                      <option value="release">Release</option>
                    </select>
                  </div>
                  <div className="space-y-1.5">
                    <Label>Amount ({selectedWallet.ccode || 'GHS'})</Label>
                    <Input
                      type="number"
                      step="any"
                      min="0"
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                    />
                  </div>
                  <div className="space-y-1.5 sm:col-span-2">
                    <Label>Reason</Label>
                    <Input
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      placeholder="Why adjust?"
                    />
                  </div>
                </div>
                <Button type="submit" className="mt-3" disabled={adjustMutation.isPending}>
                  {adjustMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Review adjustment
                </Button>
              </form>
            )}

            <div className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm">
              <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 px-5 py-3">
                <h2 className="text-sm font-semibold text-gray-800">Ledger</h2>
                <select
                  value={ledgerType}
                  onChange={(e) => setLedgerType(e.target.value)}
                  className="rounded-md border border-gray-300 bg-white px-2 py-1.5 text-sm"
                >
                  <option value="">All types</option>
                  <option value="credit">Credit</option>
                  <option value="debit">Debit</option>
                  <option value="hold">Hold</option>
                  <option value="release">Release</option>
                </select>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-100">
                  <thead>
                    <tr className="bg-gray-50">
                      {['WTID', 'Type', 'Amount', 'Balance after', 'Reference', 'Date'].map((h) => (
                        <th
                          key={h}
                          className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500"
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {ledgerLoading &&
                      Array.from({ length: 5 }).map((_, i) => (
                        <tr key={i}>
                          {Array.from({ length: 6 }).map((__, j) => (
                            <td key={j} className="px-5 py-3">
                              <Skeleton className="h-4 w-14" />
                            </td>
                          ))}
                        </tr>
                      ))}
                    {!ledgerLoading && (ledger?.transactions?.length ?? 0) === 0 && (
                      <tr>
                        <td colSpan={6} className="py-12 text-center text-sm text-gray-500">
                          No transactions
                        </td>
                      </tr>
                    )}
                    {!ledgerLoading &&
                      ledger?.transactions?.map((tx) => (
                        <tr key={tx.wtid}>
                          <td className="px-5 py-3 font-mono text-sm">{tx.wtid}</td>
                          <td className="px-5 py-3 text-sm capitalize">{tx.type || '—'}</td>
                          <td className="px-5 py-3 text-sm font-semibold">
                            {formatMoney(tx.amount, selectedWallet?.ccode)}
                          </td>
                          <td className="px-5 py-3 text-sm">
                            {formatMoney(tx.balance_after, selectedWallet?.ccode)}
                          </td>
                          <td className="max-w-[200px] truncate px-5 py-3 text-sm text-gray-600">
                            {tx.reference_type === 'payment' && tx.reference_id != null ? (
                              <RelationshipLink kind="payment" id={tx.reference_id} />
                            ) : tx.reference_type === 'invoice' && tx.reference_id != null ? (
                              <RelationshipLink kind="invoice" id={tx.reference_id} />
                            ) : (
                              tx.description || tx.reference_type || '—'
                            )}
                          </td>
                          <td className="px-5 py-3 text-sm text-gray-500">
                            {formatDate(tx.date_added)}
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
              <div className="border-t border-gray-100 px-5 py-3">
                <Pagination
                  total={ledger?.pagination?.total ?? 0}
                  page={ledgerPage}
                  itemsPerPage={LEDGER_LIMIT}
                  limit={LEDGER_LIMIT}
                  setPage={setLedgerPage}
                  showButtons
                  showSelect={false}
                />
              </div>
            </div>

            {selectedWallet && (
              <>
                <WalletPayoutConfigCard wid={selectedWallet.wid} />
                <WalletWithdrawals canAct={canAdjust} wid={selectedWallet.wid} />
              </>
            )}
          </>
        )}
        </>
        )}
      </div>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm wallet adjustment</AlertDialogTitle>
            <AlertDialogDescription>
              {direction.toUpperCase()} {formatMoney(Number(amount), selectedWallet?.ccode || ccode)}
              {selectedWallet ? ` on wallet #${selectedWallet.wid}` : ''}. Reason: “{reason.trim()}”.
              This action is recorded immediately and cannot be undone from here.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction disabled={adjustMutation.isPending} onClick={handleAdjustConfirmed}>
              {adjustMutation.isPending ? (
                <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
              ) : null}
              Confirm adjustment
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default WalletPage;
